// AYA Academy — reproduction stricte de la maquette de référence (dense, mobile-first).
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, BookOpen, Briefcase, BarChart3, Users, LayoutGrid, BadgeCheck, Tags, Radio,
  User, ArrowRight, Clock, Star, Rocket, Monitor, ShoppingCart, Wallet, Sparkles,
} from "lucide-react";
import FlagCi from "@/components/FlagCi";
import ExpHeader from "./ExpHeader";
import AyaMenu from "@/components/AyaMenu";

const IMG = "/aya/academy";
const ctx = (m) => `/aya-prive?ctx=${encodeURIComponent(m)}`;

const HERO_POINTS = [
  { Icon: BookOpen, label: "Formations pratiques" },
  { Icon: Briefcase, label: "Compétences pour l'emploi" },
  { Icon: BarChart3, label: "Développement personnel" },
  { Icon: Users, label: "Une communauté qui progresse" },
];

const SHORTCUTS = [
  { id: "domaines", Icon: LayoutGrid, label: "Formations\npar domaine", target: "aca-domaines" },
  { id: "parcours", Icon: Briefcase, label: "Parcours\nmétier", target: "aca-domaines" },
  { id: "certifs", Icon: BadgeCheck, label: "Certifications", target: "aca-cta" },
  { id: "gratuites", Icon: Tags, label: "Formations\ngratuites", target: "aca-formations" },
  { id: "direct", Icon: Radio, label: "Formations\nen direct", target: "aca-live" },
  { id: "espace", Icon: User, label: "Mon espace\napprentissage", route: "/mon-compte" },
];

const UPCOMING = [
  { id: "u1", day: "SAM", num: "28", month: "SEPT.", title: "Marketing sur TikTok pour les commerçants", time: "18h00 - 19h30", img: `${IMG}/aca_course_tiktok.jpg` },
  { id: "u2", day: "MAR", num: "01", month: "OCT.", title: "Gestion financière pour indépendants", time: "18h00 - 19h30", img: `${IMG}/aca_dom_gestion.jpg` },
  { id: "u3", day: "JEU", num: "03", month: "OCT.", title: "Informatique de base pour débutants", time: "18h00 - 19h30", img: `${IMG}/aca_course_info.jpg` },
];

const DOMAINS = [
  { id: "entrepreneuriat", Icon: Rocket, img: `${IMG}/aca_dom_entrepreneuriat.jpg`, name: "Entrepreneuriat", sub: "Créer · Gérer · Développer" },
  { id: "digital", Icon: Monitor, img: `${IMG}/aca_dom_digital.jpg`, name: "Digital & IA", sub: "Outils · Compétences · Nouvelles opportunités" },
  { id: "commerce", Icon: ShoppingCart, img: `${IMG}/aca_dom_commerce.jpg`, name: "Commerce & Vente", sub: "Vente en ligne · Live · Relation client" },
  { id: "emploi", Icon: Briefcase, img: `${IMG}/aca_dom_emploi.jpg`, name: "Emploi & Carrière", sub: "CV · Entretiens · Compétences métier" },
  { id: "gestion", Icon: Wallet, img: `${IMG}/aca_dom_gestion.jpg`, name: "Gestion & Finance", sub: "Budget · Comptabilité · Croissance" },
  { id: "viepratique", Icon: Sparkles, img: `${IMG}/aca_dom_viepratique.jpg`, name: "Vie pratique", sub: "Langues · Santé · Droit · Citoyenneté" },
];

const COURSES = [
  { id: "boutique", img: `${IMG}/aca_course_boutique.jpg`, isNew: true, title: "Créer sa boutique en ligne avec AYA", dur: "2h30", lessons: "12 leçons", rate: "4,8", votes: "(1,2K)", badge: "Gratuit" },
  { id: "tiktok", img: `${IMG}/aca_course_tiktok.jpg`, title: "TikTok Live : vendre efficacement", dur: "3h15", lessons: "14 leçons", rate: "4,7", votes: "(980)", badge: "Pro" },
  { id: "finance", img: `${IMG}/aca_course_finance.jpg`, title: "Gestion financière pour indépendants", dur: "2h00", lessons: "10 leçons", rate: "4,8", votes: "(856)", badge: "Gratuit" },
  { id: "info", img: `${IMG}/aca_course_info.jpg`, title: "Informatique de base", dur: "1h45", lessons: "8 leçons", rate: "4,6", votes: "(1,5K)", badge: "Gratuit" },
];

const TESTIMONIALS = [
  { id: "fatou", img: `${IMG}/aca_dom_commerce.jpg`, quote: "\u201CGrâce à AYA Academy j'ai appris le marketing digital et j'ai pu lancer ma boutique en ligne. Aujourd'hui je vis de mon activité.\u201D", name: "Fatou K.", role: "Commerçante, Abidjan" },
  { id: "jonathan", img: "/aya/solidarite/sol_ben_etudiant.jpg", quote: "\u201CLes formations sont simples, pratiques et vraiment utiles. J'ai obtenu mon premier emploi grâce aux conseils sur le CV et l'entretien.\u201D", name: "Jonathan B.", role: "Étudiant, Yopougon" },
  { id: "mariama", img: "/aya/solidarite/sol_ben_mariama.jpg", quote: "\u201CJ'ai appris la gestion financière et maintenant je gère mieux mon budget. Merci AYA !\u201D", name: "Mariama S.", role: "Indépendante, Cocody" },
];

export default function AcademyPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [freeOnly, setFreeOnly] = useState(false);

  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const pickShortcut = (s) => {
    if (s.id === "gratuites") setFreeOnly(true);
    if (s.route) navigate(s.route); else goTo(s.target);
  };
  const submitSearch = (e) => { e.preventDefault(); setFreeOnly(false); goTo("aca-formations"); };

  const q = query.trim().toLowerCase();
  const shown = COURSES.filter((c) => (!freeOnly || c.badge === "Gratuit") && (!q || c.title.toLowerCase().includes(q)));

  return (
    <div style={S.page} data-testid="academy-page">
      <AyaMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      <ExpHeader title="AYA Academy" testid="academy-header" onBell={() => setMenuOpen(true)} onMore={() => setMenuOpen(true)} />

      <div style={S.container}>
        {/* ── HERO ── */}
        <div style={S.hero} data-testid="academy-hero">
          <img src={`${IMG}/aca_hero.jpg`} alt="AYA Academy" style={S.heroImg} />
          <div style={S.heroOverlay} />
          <div style={S.heroContent}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
              <div>
                <div style={S.heroAya}>AYA</div>
                <div style={S.heroName}>ACADEMY</div>
              </div>
              <FlagCi size={22} style={{ marginTop: 8 }} />
            </div>
            <p style={S.heroSlogan}>Apprendre aujourd'hui<br />pour construire un meilleur<br />demain.</p>
            <div style={S.heroPoints}>
              {HERO_POINTS.map((p) => (
                <span key={p.label} style={S.heroPoint}><p.Icon size={12} color="#34D399" /> {p.label}</span>
              ))}
            </div>
          </div>
          <div style={S.heroSign} aria-hidden="true">Apprendre<br />Grandir<br />Entreprendre</div>
        </div>

        {/* ── SEARCH ── */}
        <form onSubmit={submitSearch} style={S.searchWrap} data-testid="academy-search-form">
          <Search size={15} color="#9DB4A6" style={{ flexShrink: 0 }} />
          <input style={S.searchInput} data-testid="academy-search-input" placeholder="Rechercher une formation, une compétence..."
            value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Rechercher une formation" />
        </form>

        {/* ── 6 RACCOURCIS ── */}
        <div style={S.shortcutRow} data-testid="academy-shortcuts">
          {SHORTCUTS.map((s) => (
            <button key={s.id} style={S.shortcut} data-testid={`aca-shortcut-${s.id}`} onClick={() => pickShortcut(s)}>
              <s.Icon size={19} color="#34D399" strokeWidth={2.1} />
              <span style={S.shortcutLabel}>{s.label.split("\n").map((l, i) => <span key={i}>{l}<br /></span>)}</span>
            </button>
          ))}
        </div>

        {/* ── LIVE + PROCHAINS DIRECTS ── */}
        <div id="aca-live" style={S.liveWrap} data-testid="academy-live-section">
          <div style={S.liveCard}>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
              <span style={S.liveBadge}>• LIVE</span>
              <h2 style={S.liveTitle}>Formation en direct</h2>
              <p style={S.liveSub}>Créer et développer son business en ligne avec AYA</p>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                <span style={S.avatarStack}>
                  {[TESTIMONIALS[0], TESTIMONIALS[1], TESTIMONIALS[2]].map((t, i) => (
                    <img key={t.id} src={t.img} alt="" style={{ ...S.stackImg, marginLeft: i === 0 ? 0 : -6 }} />
                  ))}
                </span>
                <span style={{ fontSize: 10, color: "#CFE5D6", fontWeight: 600 }}>2,4K participants</span>
              </div>
              <button style={S.joinBtn} data-testid="aca-live-join-btn" onClick={() => navigate(ctx("Je veux rejoindre la formation en direct AYA Academy : créer et développer son business en ligne."))}>
                Rejoindre maintenant <ArrowRight size={14} />
              </button>
            </div>
            <img src={`${IMG}/aca_live.jpg`} alt="Formation en direct" style={S.liveImg} loading="lazy" />
          </div>
          <div style={S.upcomingCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={S.upcomingTitle}>Prochains directs</h3>
              <button style={S.secMore} data-testid="aca-upcoming-see-all" onClick={() => navigate(ctx("Je veux voir toutes les prochaines formations en direct AYA Academy."))}>Voir tout</button>
            </div>
            {UPCOMING.map((u) => (
              <button key={u.id} style={S.upRow} data-testid={`aca-upcoming-${u.id}`} onClick={() => navigate(ctx(`Je veux m'inscrire au direct AYA Academy : ${u.title}.`))}>
                <span style={S.upDate}>
                  <span style={{ color: "#DC2626", fontSize: 8, fontWeight: 800 }}>{u.day}</span>
                  <span style={{ fontSize: 14, fontWeight: 900, lineHeight: 1 }}>{u.num}</span>
                  <span style={{ fontSize: 7.5, fontWeight: 800 }}>{u.month}</span>
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <span style={S.upRowTitle}>{u.title}</span>
                  <span style={S.upRowTime}><Clock size={10} color="#34D399" /> {u.time}</span>
                </span>
                <img src={u.img} alt="" style={S.upThumb} loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        {/* ── DOMAINES ── */}
        <div id="aca-domaines" style={S.secHead}>
          <h2 style={S.secTitle}>Nos domaines de formation</h2>
          <button style={S.secMore} data-testid="aca-domains-see-all" onClick={() => navigate(ctx("Je veux voir tous les domaines de formation AYA Academy."))}>Voir tous les domaines <ArrowRight size={13} /></button>
        </div>
        <div style={S.hScroll} data-testid="academy-domains-row">
          {DOMAINS.map((d) => (
            <button key={d.id} style={S.domCard} data-testid={`aca-domain-${d.id}`} onClick={() => navigate(ctx(`Je veux découvrir les formations AYA Academy du domaine ${d.name}.`))}>
              <span style={S.domImgBox}>
                <img src={d.img} alt={d.name} style={S.domImg} loading="lazy" />
                <span style={S.domOverlay}><d.Icon size={12} color="#34D399" /> {d.name}</span>
              </span>
              <span style={S.domSub}>{d.sub}</span>
            </button>
          ))}
        </div>

        {/* ── FORMATIONS POPULAIRES ── */}
        <div id="aca-formations" style={S.secHead}>
          <h2 style={S.secTitle}>Formations populaires</h2>
          <button style={S.secMore} data-testid="aca-courses-see-all" onClick={() => { setFreeOnly(false); setQuery(""); }}>Voir tout <ArrowRight size={13} /></button>
        </div>
        <div style={S.hScroll} data-testid="academy-courses-row">
          {shown.length === 0 && <div style={{ color: "#9DB4A6", fontSize: 12, padding: "10px 2px" }} data-testid="aca-courses-empty">Aucune formation trouvée.</div>}
          {shown.map((c) => (
            <button key={c.id} style={S.courseCard} data-testid={`aca-course-${c.id}`} onClick={() => navigate(ctx(`Je veux suivre la formation AYA Academy : ${c.title}.`))}>
              <span style={{ position: "relative", display: "block" }}>
                <img src={c.img} alt={c.title} style={S.courseImg} loading="lazy" />
                {c.isNew && <span style={S.newBadge}>NOUVEAU</span>}
              </span>
              <span style={S.courseBody}>
                <span style={S.courseTitle}>{c.title}</span>
                <span style={S.courseMeta}><Clock size={10} /> {c.dur} <BookOpen size={10} style={{ marginLeft: 5 }} /> {c.lessons}</span>
                <span style={S.courseFoot}>
                  <span style={S.courseRate}><Star size={10} color="#F5A623" fill="#F5A623" /> {c.rate} <span style={{ color: "#9DB4A6", fontWeight: 600 }}>{c.votes}</span></span>
                  <span style={{ ...S.courseBadge, ...(c.badge === "Pro" ? S.badgePro : {}) }}>{c.badge}</span>
                </span>
              </span>
            </button>
          ))}
        </div>

        {/* ── CTA CERTIFICAT / FORMATEUR ── */}
        <div id="aca-cta" style={S.ctaGrid} data-testid="academy-cta-section">
          <div style={S.ctaGreen}>
            <div style={S.ctaRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={S.ctaTitle}>Obtenez votre certificat AYA</h3>
                <p style={S.ctaDesc}>Validez vos compétences et boostez votre employabilité.</p>
              </div>
              <img src={`${IMG}/aca_certificat.jpg`} alt="Certificat AYA" style={S.ctaImg} loading="lazy" />
            </div>
            <button style={S.ctaBtnGreen} data-testid="aca-cta-certif-btn" onClick={() => navigate(ctx("Je veux obtenir un certificat AYA Academy : voir les certifications disponibles."))}>
              Voir les certifications <ArrowRight size={13} />
            </button>
          </div>
          <div style={S.ctaAmber}>
            <div style={S.ctaRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ ...S.ctaTitle, color: "#FBBF24" }}>Devenez formateur AYA</h3>
                <p style={S.ctaDesc}>Partagez votre expertise et inspirez la communauté.</p>
              </div>
              <img src={`${IMG}/aca_formateur.jpg`} alt="Formateur AYA" style={S.ctaImg} loading="lazy" />
            </div>
            <button style={S.ctaBtnAmber} data-testid="aca-cta-trainer-btn" onClick={() => navigate(ctx("Je veux devenir formateur AYA Academy et proposer une formation."))}>
              Proposer une formation <ArrowRight size={13} />
            </button>
          </div>
        </div>

        {/* ── TÉMOIGNAGES ── */}
        <div style={S.secHead}>
          <h2 style={S.secTitle}>Témoignages d'apprenants</h2>
          <button style={S.secMore} data-testid="aca-testimonials-see-more" onClick={() => navigate(ctx("Je veux lire plus de témoignages d'apprenants AYA Academy."))}>Voir plus <ArrowRight size={13} /></button>
        </div>
        <div style={S.hScroll} data-testid="academy-testimonials-row">
          {TESTIMONIALS.map((t) => (
            <div key={t.id} style={S.testiCard} data-testid={`aca-testimonial-${t.id}`}>
              <p style={S.testiQuote}>{t.quote}</p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img src={t.img} alt={t.name} style={S.testiAvatar} loading="lazy" />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 11, fontWeight: 800 }}>{t.name}</span>
                  <span style={{ display: "block", fontSize: 9, color: "#9DB4A6" }}>{t.role}</span>
                </span>
                <span style={{ display: "inline-flex", gap: 1 }}>
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={9} color="#F5A623" fill="#F5A623" />)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "radial-gradient(ellipse at top, #072115 0%, #04120B 60%, #030B07 100%)", color: "#ECF5EE", paddingBottom: 26, overflowX: "hidden" },
  container: { maxWidth: 880, margin: "0 auto", padding: "0 12px", minWidth: 0 },
  hero: { position: "relative", borderRadius: 18, overflow: "hidden", height: 236, marginTop: 2, border: "1px solid rgba(16,185,129,0.25)" },
  heroImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "right center" },
  heroOverlay: { position: "absolute", inset: 0, background: "linear-gradient(95deg, rgba(3,20,12,0.93) 26%, rgba(3,20,12,0.45) 58%, rgba(3,20,12,0.1))" },
  heroContent: { position: "relative", padding: "14px 14px" },
  heroAya: { fontSize: 32, fontWeight: 900, color: "#FFFFFF", lineHeight: 0.95, letterSpacing: 1 },
  heroName: { fontSize: 23, fontWeight: 900, color: "#2FCF62", lineHeight: 1.05, letterSpacing: 0.5 },
  heroSlogan: { margin: "9px 0 0", fontSize: 12.5, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.3 },
  heroPoints: { display: "flex", flexDirection: "column", gap: 4, marginTop: 9 },
  heroPoint: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, color: "#E8F6EC", fontWeight: 600 },
  heroSign: { position: "absolute", right: 12, top: 12, transform: "rotate(-7deg)", textAlign: "right", pointerEvents: "none", fontFamily: "'Caveat', cursive", fontWeight: 700, fontSize: 18, lineHeight: 1.05, color: "#2FCF62", textShadow: "0 1px 8px rgba(0,0,0,0.6)" },
  searchWrap: { display: "flex", alignItems: "center", gap: 8, marginTop: 10, height: 40, padding: "0 14px", borderRadius: 999, background: "rgba(6,34,22,0.9)", border: "1.5px solid rgba(16,185,129,0.4)" },
  searchInput: { flex: 1, background: "transparent", border: "none", outline: "none", color: "#ECF5EE", fontSize: 12.5, minWidth: 0 },
  shortcutRow: { display: "flex", gap: 7, marginTop: 9, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 2 },
  shortcut: { flex: "0 0 auto", minWidth: 94, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "11px 8px 9px", borderRadius: 13, background: "rgba(8,38,24,0.65)", border: "1px solid rgba(16,185,129,0.3)", cursor: "pointer", color: "#ECF5EE" },
  shortcutLabel: { fontSize: 9.5, fontWeight: 700, lineHeight: 1.2, textAlign: "center" },
  liveWrap: { display: "flex", flexWrap: "wrap", gap: 9, marginTop: 11, scrollMarginTop: 56 },
  liveCard: { flex: "1.5 1 300px", display: "flex", gap: 10, padding: 11, borderRadius: 16, background: "rgba(8,38,24,0.7)", border: "1px solid rgba(16,185,129,0.35)" },
  liveBadge: { alignSelf: "flex-start", padding: "3px 8px", borderRadius: 8, background: "#DC2626", color: "#fff", fontSize: 9, fontWeight: 800, letterSpacing: 0.4 },
  liveTitle: { margin: "7px 0 0", fontSize: 17, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.15 },
  liveSub: { margin: "5px 0 0", fontSize: 10.5, color: "#CFE5D6", lineHeight: 1.35 },
  avatarStack: { display: "inline-flex", flexShrink: 0 },
  stackImg: { width: 18, height: 18, borderRadius: "50%", objectFit: "cover", border: "1.5px solid #06281A" },
  joinBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, height: 33, padding: "0 14px", borderRadius: 999, border: "none", background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", fontSize: 11, fontWeight: 800, cursor: "pointer", marginTop: "auto", alignSelf: "flex-start" },
  liveImg: { flex: "0 0 42%", maxWidth: 250, minHeight: 130, borderRadius: 12, objectFit: "cover", alignSelf: "stretch" },
  upcomingCard: { flex: "1 1 250px", display: "flex", flexDirection: "column", gap: 7, padding: 10, borderRadius: 16, background: "rgba(8,38,24,0.55)", border: "1px solid rgba(16,185,129,0.25)" },
  upcomingTitle: { margin: 0, fontSize: 13, fontWeight: 800, color: "#FFFFFF" },
  upRow: { display: "flex", alignItems: "center", gap: 8, padding: 7, borderRadius: 12, background: "rgba(6,34,22,0.7)", border: "1px solid rgba(16,185,129,0.2)", cursor: "pointer", color: "#ECF5EE" },
  upDate: { display: "flex", flexDirection: "column", alignItems: "center", padding: "3px 7px", borderRadius: 8, background: "#FFFFFF", color: "#0B3B27", flexShrink: 0 },
  upRowTitle: { display: "block", fontSize: 10.5, fontWeight: 800, lineHeight: 1.25 },
  upRowTime: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9.5, color: "#9DB4A6", fontWeight: 600, marginTop: 2 },
  upThumb: { width: 38, height: 38, borderRadius: 9, objectFit: "cover", flexShrink: 0 },
  secHead: { display: "flex", justifyContent: "space-between", alignItems: "center", margin: "14px 0 8px", gap: 8, scrollMarginTop: 56, minWidth: 0 },
  secTitle: { fontSize: 15.5, fontWeight: 800, margin: 0, color: "#FFFFFF", minWidth: 0 },
  secMore: { display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", color: "#34D399", fontSize: 11.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 },
  hScroll: { display: "flex", gap: 9, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 3, alignItems: "stretch" },
  domCard: { flex: "0 0 34%", minWidth: 132, maxWidth: 168, display: "flex", flexDirection: "column", gap: 4, padding: 6, borderRadius: 13, background: "rgba(8,38,24,0.65)", border: "1px solid rgba(16,185,129,0.28)", cursor: "pointer", color: "#ECF5EE", textAlign: "left" },
  domImgBox: { position: "relative", display: "block", height: 84, borderRadius: 10, overflow: "hidden" },
  domImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" },
  domOverlay: { position: "absolute", left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", gap: 4, padding: "12px 7px 5px", fontSize: 10, fontWeight: 800, color: "#FFFFFF", background: "linear-gradient(transparent, rgba(3,15,9,0.92))" },
  domSub: { fontSize: 8.8, color: "#9DB4A6", lineHeight: 1.3, padding: "0 2px 2px" },
  courseCard: { flex: "0 0 44%", minWidth: 168, maxWidth: 212, display: "flex", flexDirection: "column", padding: 0, borderRadius: 14, overflow: "hidden", background: "rgba(8,38,24,0.7)", border: "1px solid rgba(16,185,129,0.28)", cursor: "pointer", color: "#ECF5EE", textAlign: "left" },
  courseImg: { width: "100%", height: 88, objectFit: "cover", display: "block" },
  newBadge: { position: "absolute", top: 7, left: 7, padding: "3px 8px", borderRadius: 7, background: "#F5A623", color: "#14100A", fontSize: 8, fontWeight: 800, letterSpacing: 0.5 },
  courseBody: { display: "flex", flexDirection: "column", gap: 4, padding: "8px 9px 9px", flex: 1 },
  courseTitle: { fontSize: 11.5, fontWeight: 800, lineHeight: 1.25, flex: 1 },
  courseMeta: { display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9.5, color: "#9DB4A6", fontWeight: 600 },
  courseFoot: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 },
  courseRate: { display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9.5, fontWeight: 800, color: "#F5A623" },
  courseBadge: { padding: "3px 9px", borderRadius: 7, background: "rgba(16,185,129,0.22)", border: "1px solid rgba(16,185,129,0.5)", color: "#A7F3D0", fontSize: 8.5, fontWeight: 800 },
  badgePro: { background: "rgba(245,166,35,0.18)", borderColor: "rgba(245,166,35,0.55)", color: "#FBBF24" },
  ctaGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 9, marginTop: 14, scrollMarginTop: 56 },
  ctaGreen: { display: "flex", flexDirection: "column", gap: 8, padding: 11, borderRadius: 16, background: "rgba(8,38,24,0.8)", border: "1px solid rgba(16,185,129,0.4)" },
  ctaAmber: { display: "flex", flexDirection: "column", gap: 8, padding: 11, borderRadius: 16, background: "linear-gradient(150deg, rgba(66,40,8,0.85), rgba(30,18,4,0.9))", border: "1px solid rgba(245,166,35,0.45)" },
  ctaRow: { display: "flex", gap: 8, flex: 1, minWidth: 0 },
  ctaTitle: { margin: 0, fontSize: 13, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.2 },
  ctaDesc: { margin: "5px 0 0", fontSize: 9.5, color: "#CFE5D6", lineHeight: 1.35 },
  ctaBtnGreen: { display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: 30, width: "100%", padding: "0 6px", borderRadius: 999, border: "none", background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", fontSize: 9.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" },
  ctaBtnAmber: { display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: 30, width: "100%", padding: "0 6px", borderRadius: 999, border: "none", background: "linear-gradient(135deg,#F5A623,#D97706)", color: "#14100A", fontSize: 9.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" },
  ctaImg: { width: 50, height: 66, borderRadius: 10, objectFit: "cover", flexShrink: 0, alignSelf: "center" },
  testiCard: { flex: "0 0 62%", minWidth: 210, maxWidth: 268, display: "flex", flexDirection: "column", gap: 8, padding: 11, borderRadius: 14, background: "rgba(8,38,24,0.65)", border: "1px solid rgba(16,185,129,0.25)" },
  testiQuote: { margin: 0, fontSize: 10, color: "#CFE5D6", lineHeight: 1.45, flex: 1 },
  testiAvatar: { width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
};
